import type { ReactNode } from 'react';
import {
  Box,
  Button,
  InlineStack,
  Paragraph,
  PopoverContent,
  ProgressBar,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
} from '@customerio/pluma-components/react';
import { FlexMark, leadingMarkButton } from '@/components/flex-mark';
import * as styles from './addon-popover.css';
import { FlexAddonBadge } from './flex-addon-badge';

type AddonPopoverProps = {
  title: string;
  children: ReactNode;
};

export function AddonPopover({ title, children }: AddonPopoverProps) {
  return (
    <PopoverContent className={styles.popoverContent}>
      <Stack gap="150">
        <Text text="product-p-active">{title}</Text>
        {children}
      </Stack>
    </PopoverContent>
  );
}

type AddonIncludedBoxProps = {
  label: string;
  figure: ReactNode;
  children?: ReactNode;
};

export function AddonIncludedBox({ label, figure, children }: AddonIncludedBoxProps) {
  return (
    <Stack className={styles.includedBox} gap="100">
      <InlineStack gap="150" alignBlock="center" alignInline="space-between">
        <Text text="product-psm" color="secondary">
          {label}
        </Text>
        <Text text="product-psm-active">{figure}</Text>
      </InlineStack>
      {children}
    </Stack>
  );
}

type AddonUsageBarProps = {
  share: number;
  ariaLabel: string;
};

export function AddonUsageBar({ share, ariaLabel }: AddonUsageBarProps) {
  return <ProgressBar className={styles.bar} value={Math.min(1, Math.max(0, share)) * 100} ariaLabel={ariaLabel} />;
}

type AddonOfferBoxProps = {
  label: string;
  price: string | null;
  value: string;
};

export function AddonOfferBox({ label, price, value }: AddonOfferBoxProps) {
  return (
    <InlineStack className={styles.offerBox} gap="150" alignBlock="center" alignInline="space-between">
      <Stack>
        <InlineStack gap="075" alignBlock="baseline" wrap="wrap">
          <Text text="product-psm">{label}</Text>
          {price == null ? null : (
            <Text size="xs" color="secondary">
              {price}
            </Text>
          )}
        </InlineStack>
        <Paragraph text="product-psm-active">{value}</Paragraph>
      </Stack>
      <Box flexShrink="0">
        <FlexAddonBadge />
      </Box>
    </InlineStack>
  );
}

type AddonNoteProps = { children: ReactNode };

export function AddonNote({ children }: AddonNoteProps) {
  return <Paragraph text="product-pxs-secondary">{children}</Paragraph>;
}

type AddonActionProps = {
  label: string;
  onClick: () => void;
  blockedReason?: string | null;
};

export function AddonAction({ label, onClick, blockedReason = null }: AddonActionProps) {
  if (blockedReason == null) {
    return (
      <InlineStack>
        <Button
          className={leadingMarkButton}
          variant="primary"
          size="sm"
          elementBefore={<FlexMark tone="solid" />}
          onClick={onClick}
        >
          {label}
        </Button>
      </InlineStack>
    );
  }

  return (
    <InlineStack>
      <Tooltip isCustom placement="top">
        {(context) => (
          <>
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              className={leadingMarkButton}
              variant="primary"
              size="sm"
              elementBefore={<FlexMark tone="solid" />}
              isDisabled
              onClick={onClick}
            >
              {label}
            </Button>
            <TooltipContent>{blockedReason}</TooltipContent>
          </>
        )}
      </Tooltip>
    </InlineStack>
  );
}
